import type { MetricChartSpec } from '@/components/charts/generic';
import type { ThresholdRange, TiltDataPoint } from '@/domain/behavior';
import type { Locale } from '@/utils/localeFormatters';

import {
    BEHAVIOR_GREEN,
    BEHAVIOR_LINE_MARKER,
    BEHAVIOR_LINE_TENSION,
    behaviorPoints,
    behaviorXAxis,
    measurementsFooter,
    tiltGradient,
} from '../behaviorChartSpec';
import { type BehaviorContext, contextSeries } from '../contextSeries/contextSeries';

export const TILT_MIN = -20;
export const TILT_MAX = 20;

export interface TiltSpecInput {
    points: TiltDataPoint[];
    threshold: ThresholdRange;
    /** The reading on the right axis. */
    context: BehaviorContext;
    t: (key: string) => string;
    locale: Locale;
}

/**
 * Tilt in degrees on a fixed ±20° axis, coloured by its threshold, with a context band on the
 * right and the day's measurement count in the tooltip footer.
 */
export function buildTiltSpec(input: TiltSpecInput): MetricChartSpec<'line'> {
    const { points, threshold, context, t, locale } = input;
    const right = contextSeries(points, context, t);
    const label = t('tiltView.title');
    return {
        kind: 'line',
        ...behaviorXAxis(points, locale),
        series: [
            {
                key: 'tilt',
                label,
                color: BEHAVIOR_GREEN,
                lineGradient: tiltGradient(threshold),
                tension: BEHAVIOR_LINE_TENSION,
                marker: BEHAVIOR_LINE_MARKER,
                data: behaviorPoints(points, (point) => point.tilt),
            },
            right.series,
        ],
        left: { label, unit: '°', min: TILT_MIN, max: TILT_MAX },
        right: right.axis,
        locale,
        tooltipFooterLines: measurementsFooter(
            points,
            (point) => point.numberOfMeasurements,
            () => t('common.measurements'),
            locale
        ),
    };
}
